// Pending invite grants on a project's Members tab (architecture sprint-12 F4, F5): cancel a grant and
// renew (New link). Both use the oversight endpoints, which Admins pass too.
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { apiRequest, apiRequestNoContent } from '@/lib/api';
import type { ProjectInvite } from '@/types/settings';
import { invalidateMembership } from './use-project-members';

const ADMIN_INVITES_KEY = ['admin', 'invites'] as const;

function useInvalidateInvites(projectId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await invalidateMembership(queryClient, projectId);
    await queryClient.invalidateQueries({ queryKey: ADMIN_INVITES_KEY });
  };
}

export function useCancelInviteGrant(projectId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateInvites(projectId);
  return useMutation({
    mutationFn: (grantId) => apiRequestNoContent(`/api/oversight/projects/${projectId}/invite-grants/${grantId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useRenewInviteGrant(projectId: string): UseMutationResult<{ invite: ProjectInvite; link: string }, Error, string> {
  const invalidate = useInvalidateInvites(projectId);
  return useMutation({
    mutationFn: (grantId) => apiRequest(`/api/oversight/projects/${projectId}/invite-grants/${grantId}/link`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}
